import { useCallback, useEffect, useState, useTransition, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../components/Filters/components/NoResults';
import { API } from '../../../API';
import { showRequestError } from '../../../utils/requestError';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '../contexts';


export interface CountryOption {
  value: number | string;
  label: string;
}

export interface CountriesResponse {
  objects: CountryData[];
  total_count: number;
}


export interface CountryData {
  id: number;
  name: string;
  code: string;
  sources_count: number;
}

export const CountryChooser = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters } = useSearchContext();
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<CountryOption[]>([]);
  const [allLoadedCountries, setAllLoadedCountries] = useState<CountryOption[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isPending, startTransition] = useTransition();

  // Use draft filters from context - need to convert string[] to number[]
  const selectedCountries: (string | number)[] = state.draftFilters.countries.value.map(c => parseInt(c));

  const getAllCountries = useCallback(() => {
      startTransition(() => {
        API.fetch('GET', `/API/v1/countries`)
          .then((data: CountriesResponse) => {
            let optionsList = data.objects
              .slice(0, 10)
              .map((country: CountryData) => {
                return {value: country.id, label: country.name};
              })
              setDefaultOptions(optionsList)
              // Store all loaded countries
              setAllLoadedCountries(prev => {
                const existingIds = new Set(prev.map(c => c.value));
                const newCountries = optionsList.filter(c => !existingIds.has(c.value));
                return [...prev, ...newCountries];
              });
          })
          .catch((error) => {
            showRequestError(error, t('Countries'));
          });
      });
    }, []);

    useEffect(getAllCountries, [getAllCountries]);

    useEffect(() => {
      if (selectedCountries.length === 0) return;

      const missingIds = selectedCountries.filter(id =>
        !allLoadedCountries.some(c => c.value === id)
      );

      if (missingIds.length === 0) return;

      missingIds.forEach(countryId => {
        API.fetch('GET', `/API/v1/countries/${countryId}`)
          .then((country: CountryData) => {
            const countryOption = {
              value: country.id,
              label: country.name
            };
            setAllLoadedCountries(prev => {
              if (prev.some(c => c.value === country.id)) {
                return prev;
              }
              return [...prev, countryOption];
            });
          })
          .catch((error) => {
            showRequestError(error, t('Countries'));
          });
      });
    }, [selectedCountries.length]);

    const getCountries = useCallback((inputValue: string) => {
        if (!inputValue) {
          setCountries([]);
          return;
        }

        startTransition(() => {
          API.fetch('GET', `/API/v1/countries?q=${inputValue}`)
            .then((data: CountriesResponse) => {
              let optionsList = data.objects
                .map((country: CountryData) => {
                  return {value: country.id, label: country.name};
                })
              setCountries(optionsList);
              // Store all loaded countries
              setAllLoadedCountries(prev => {
                const existingIds = new Set(prev.map(c => c.value));
                const newCountries = optionsList.filter(c => !existingIds.has(c.value));
                return [...prev, ...newCountries];
              });
            })
            .catch((error) => {
              showRequestError(error, t('Countries'));
            });
        });
      }, []);

    const debouncedSearchRef = useRef(
      debounce((value: string) => {
        getCountries(value);
      }, 500)
    );

    // Update the debounced function when getCountries changes
    useEffect(() => {
      debouncedSearchRef.current = debounce((value: string) => {
        getCountries(value);
      }, 500);
    }, [getCountries]);

    useEffect(() => {
      const debouncedSearch = debouncedSearchRef.current;
      return () => {
        debouncedSearch.cancel();
      };
    }, []);

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      debouncedSearchRef.current(value);
    };

  const handleClearAll = () => {
    updateDraftFilters({
      countries: {
        value: [],
        exclude: false
      }
    });
  };

  const handleToggleCountry = useCallback((countryId: number | string) => {
    const currentCountries = state.draftFilters.countries.value;
    const countryIdStr = String(countryId);

    if (currentCountries.includes(countryIdStr)) {
      updateDraftFilters({
        countries: {
          value: currentCountries.filter(id => id !== countryIdStr),
          exclude: state.draftFilters.countries.exclude
        }
      });
    } else {
      updateDraftFilters({
        countries: {
          value: [...currentCountries, countryIdStr],
          exclude: state.draftFilters.countries.exclude
        }
      });
    }
  }, [state.draftFilters.countries, updateDraftFilters]);

  const listToShow = countries.length > 0 ? countries : defaultOptions;
  const isNoResults = !isPending && searchValue && countries.length === 0;

  // Memoize the checkbox items to prevent recreating on every render
  const checkboxItems = useMemo(() => {
    return listToShow.map((country: CountryOption) => ({
      name: country.label,
      checked: selectedCountries.includes(typeof country.value === 'string' ? parseInt(country.value) : country.value),
      onChange: () => handleToggleCountry(country.value),
      disabled: false,
      icon: null,
    }));
  }, [listToShow, selectedCountries, handleToggleCountry]);

  // Memoize badges to prevent recreating on every render
  const badges = useMemo(() => {
    return selectedCountries.map(countryId => {
      // Look up country name from all loaded countries
      const country = allLoadedCountries.find((c: CountryOption) => c.value === countryId);
      const countryName = country ? country.label : String(countryId);
      return {
        name: countryName,
        onChange: () => handleToggleCountry(countryId)
      };
    });
  }, [selectedCountries, allLoadedCountries, handleToggleCountry]);

  return(
    <ContentContainer
      searchComponent={
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('Search countries')}
          hint={t('Use search to find options not shown in the list')}
          id='country-search'
        />
      }
      actionsComponent={
        <ControlsPanel
          id='country-filter'
          count={selectedCountries.length}
          isExclude={state.draftFilters.countries.exclude}
          showExcludeSwitch={true}
          showSelectAll={false}
          onClearAll={handleClearAll}
          onExcludeClick={() => {
            updateDraftFilters({
              countries: {
                value: state.draftFilters.countries.value,
                exclude: !state.draftFilters.countries.exclude
              }
            });
          }}
        />
      }
      badgesComponent={
        selectedCountries.length > 0 ? (
          <ItemBadgesContainer badges={badges} />
        ) : undefined
      }
    >
      {isPending ? <FilterLoader /> : isNoResults ? <NoResults /> : (
        <CheckboxList items={checkboxItems} />
      )}
    </ContentContainer>
  )
};
